import { useCallback, useEffect, useState } from "react";
import styles from "./integration-btn.module.css";
import { ButtonGroup } from "react-bootstrap";
import { useAtom } from "jotai";
import { mondayIntegrationPopupAtom, SidepanelType, sidePanelType } from "state-atoms";
import { ToolbarProps } from "elements/base/provider";
import { GanttElement } from "elements/gantt/schema";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { SupportedGanttElements, GanttMondayItemElement } from "elements/gantt/types";
import { integrationItemPrefix } from "shared/datamodel/schemas/integration-item";
import { taskCardPrefix } from "shared/datamodel/task-card";
import { ReadTransaction } from "@workcanvas/reflect";
import { taskPrefix } from "shared/datamodel/task";
import { mondayTaskPrefix } from "elements/monday-task/schema";

type IntegrationButtonProps = ToolbarProps<GanttElement>;

export const IntegrationButton = ({ ids, context }: IntegrationButtonProps) => {
  const { reflect, documentId } = context;
  const { tasksIntegrations } = useBoardTasks(documentId);
  const [tasks, setTasks] = useState<SupportedGanttElements[]>([]);
  const id = ids[0];

  const queryItemsOnGantt = useCallback(
    async (tx: ReadTransaction) => {
      const allItems = await tx.scan().entries().toArray();
      const tasks = allItems.filter(
        ([id]) =>
          id.startsWith(taskPrefix) ||
          id.startsWith(taskCardPrefix) ||
          id.startsWith(integrationItemPrefix) ||
          id.startsWith(mondayTaskPrefix)
      );

      return tasks
        .map(([, value]) => value as SupportedGanttElements)
        .filter((task) => !task.hidden && task.containerId === id);
    },
    [id]
  );

  useEffect(() => {
    reflect?.query(queryItemsOnGantt).then((tasks) => {
      setTasks(tasks);
    });
  }, [reflect, queryItemsOnGantt, ids]);

  const [, setShowMondayIntegrationPopup] = useAtom(mondayIntegrationPopupAtom);
  const [, setOpenedSidePanel] = useAtom(sidePanelType);

  const onClick = () => {
    // if no integrations
    setOpenedSidePanel(SidepanelType.integrations);

    if (!tasksIntegrations?.length) {
      return;
    }

    const integrationId =
      (
        tasks.find((task) => task.type === "integrationItem" || task.type === "mondayTask") as
          | GanttMondayItemElement
          | undefined
      )?.integrationId || tasksIntegrations?.at(0)?.id;

    // if has integrations open first integration settings
    setShowMondayIntegrationPopup({
      isOpen: true,
      selectedResult: "map",
      integrationId: integrationId,
    });
  };

  return (
    <ButtonGroup onClick={onClick}>
      <div className={styles.wrapper}>
        <button className={styles.button}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="19" fill="none" viewBox="0 0 24 19">
            <mask
              id="mask0_1660_1646"
              width="17"
              height="17"
              x="1"
              y="1"
              maskUnits="userSpaceOnUse"
              style={{ maskType: "alpha" }}
            >
              <path
                fill="#fff"
                stroke="#fff"
                strokeWidth="0.1"
                d="M13.528 2.502c.27.07.602.258.896.484.336.259.635.579.736.856l.04.132c.075.298.016.527-.065.745-.094.25-.21.476-.207.787l.016.19c.073.444.374.87.781 1.046l.176.06q.09.025.18.04c.118.022.248.038.368.063.248.05.494.14.714.402l.07.098c.066.106.122.238.168.384.061.194.106.416.13.64.023.223.027.45.008.652a1.7 1.7 0 0 1-.085.41l-.047.109c-.213.402-.604.523-.938.69-.17.086-.33.184-.46.334-.131.15-.235.356-.288.66l-.019.177c-.014.396.172.675.364.96.218.322.445.653.33 1.152l-.023.078q-.046.125-.133.275a4 4 0 0 1-.277.417 4.3 4.3 0 0 1-.47.544l-.133.111a1.2 1.2 0 0 1-.564.233c-.174.02-.335-.01-.491-.057a5 5 0 0 1-.233-.077 6 6 0 0 0-.235-.08 1.8 1.8 0 0 0-.377-.076l-.136-.004c-.398.009-.79.235-1.028.558l-.092.144c-.152.276-.156.531-.202.796a1.086 1.086 0 0 1-.353.679l-.111.093c-.108.082-.264.15-.44.206-.178.055-.38.097-.585.123-.203.027-.41.039-.595.032a2 2 0 0 1-.376-.05l-.102-.032c-.5-.194-.633-.605-.814-.965a1.5 1.5 0 0 0-.362-.493 1.3 1.3 0 0 0-.509-.254l-.242-.049c-.404-.06-.697.129-.998.333-.26.177-.528.368-.87.38l-.151-.008a1.6 1.6 0 0 1-.475-.164 3.7 3.7 0 0 1-.528-.33 3.5 3.5 0 0 1-.467-.41 1.6 1.6 0 0 1-.238-.316l-.047-.1c-.177-.468.007-.868.125-1.254.06-.195.106-.39.09-.594a1.14 1.14 0 0 0-.155-.491l-.118-.18c-.291-.386-.637-.47-.989-.541-.306-.063-.624-.115-.889-.369l-.109-.121a1.4 1.4 0 0 1-.21-.444 3.5 3.5 0 0 1-.122-.596 3.7 3.7 0 0 1-.026-.608c.01-.188.038-.359.09-.484l.077-.153c.2-.328.532-.463.837-.616.176-.088.346-.182.488-.32s.26-.322.329-.591l.03-.142a1.03 1.03 0 0 0-.115-.722c-.062-.113-.14-.219-.221-.335q-.062-.086-.123-.182l-.116-.206c-.194-.393-.14-.79.027-1.151.165-.36.442-.69.7-.954l.093-.087a1.1 1.1 0 0 1 .633-.292c.236-.022.468.038.694.105.457.137.901.307 1.415.01l.166-.112c.346-.273.43-.633.513-1.002.094-.419.187-.852.694-1.117l.089-.04c.226-.092.568-.154.906-.176.194-.012.39-.012.564.004s.33.048.443.1l.107.056c.233.142.356.344.454.553.114.242.193.48.365.672l.125.121c.313.262.751.373 1.154.307l.112-.025c.253-.072.447-.233.66-.384.243-.171.506-.324.89-.27zM9.27 2.198c-.163-.103-.41-.107-.675-.072a5 5 0 0 0-.39.072c-.118.025-.231.05-.328.067-.152.138-.212.323-.255.535-.033.159-.057.337-.107.51l-.06.172c-.362.842-1.306 1.357-2.206 1.262l-.178-.028c-.098-.02-.2-.056-.3-.096-.101-.04-.2-.082-.3-.114-.172-.055-.324-.074-.453 0l-.054.037c-.074.061-.224.236-.366.422a5 5 0 0 0-.26.378l-.036.078a.6.6 0 0 0-.042.235c.004.06.023.109.052.155.029.048.067.092.112.145s.094.111.139.184c.596.974.519 2.167-.36 2.91l-.188.141c-.112.078-.226.13-.332.172-.107.044-.202.076-.286.12a.5.5 0 0 0-.199.167.66.66 0 0 0-.096.326l.01.203a8 8 0 0 0 .094.643q.013.068.026.117a.4.4 0 0 0 .021.064l.002.005q.05.091.168.15c.08.04.179.069.286.092s.22.04.327.06c.107.018.21.037.296.067l.17.069c.838.388 1.378 1.378 1.224 2.295l-.032.137q-.021.071-.047.145-.026.072-.055.146l-.053.147q-.054.146-.079.277a.5.5 0 0 0 .004.226l.043.071q.037.047.094.106a4.593 4.593 0 0 0 .59.475c.096.065.174.109.217.124l.138.04q.061.01.109.006a.34.34 0 0 0 .162-.056c.05-.033.098-.076.157-.126.057-.05.122-.105.204-.156l.224-.124c.526-.258 1.074-.304 1.57-.16.496.143.938.474 1.254.963l.129.219.057.129q.024.06.042.113c.024.071.042.127.07.173.026.044.06.08.119.11a.8.8 0 0 0 .277.067l.079.001q.054-.003.13-.01a7 7 0 0 0 .793-.127.4.4 0 0 0 .069-.024l.043-.028a.4.4 0 0 0 .102-.14c.039-.08.067-.18.09-.286.023-.108.04-.22.058-.327.019-.106.04-.209.068-.292l.07-.175c.397-.859 1.397-1.392 2.332-1.215l.13.033q.069.021.139.047l.282.104q.142.051.268.075a.46.46 0 0 0 .216-.007l.069-.043a1 1 0 0 0 .105-.096 4.725 4.725 0 0 0 .48-.592c.065-.096.109-.172.123-.214l.037-.131a.4.4 0 0 0 .006-.1.3.3 0 0 0-.046-.148c-.028-.045-.065-.087-.108-.138l-.066-.08-.068-.096c-.369-.598-.487-1.232-.352-1.805.135-.574.521-1.081 1.15-1.428l.17-.074c.059-.022.114-.04.168-.063.105-.044.191-.1.242-.203l.02-.053q.027-.096.032-.258a3 3 0 0 0-.027-.465 2.3 2.3 0 0 0-.101-.444 1 1 0 0 0-.074-.16.3.3 0 0 0-.075-.085c-.174-.066-.347-.097-.526-.131a3 3 0 0 1-.414-.1l-.142-.053c-.803-.346-1.335-1.261-1.271-2.131l.02-.174c.015-.088.045-.184.08-.28q.025-.074.053-.147l.054-.148c.034-.097.063-.19.079-.277a.6.6 0 0 0 .007-.176l-.01-.05a.5.5 0 0 0-.096-.135 2 2 0 0 0-.187-.18 4 4 0 0 0-.21-.168l-.165-.118c-.19-.123-.325-.185-.43-.207a.34.34 0 0 0-.247.034c-.076.04-.147.105-.246.185a2 2 0 0 1-.262.184l-.113.061a2.19 2.19 0 0 1-2.63-.557l-.121-.158a1.4 1.4 0 0 1-.113-.212c-.037-.081-.075-.172-.111-.257a3 3 0 0 0-.11-.231 1 1 0 0 0-.052-.08z"
              ></path>
              <path
                fill="#fff"
                stroke="#fff"
                strokeWidth="0.1"
                d="M10.05 6.258c2.12.45 3.251 2.698 2.276 4.654l-.104.195c-1.371 2.376-4.932 1.99-5.841-.508l-.08-.248C5.688 8.145 7.547 5.874 9.836 6.22zm-.325.75C8.22 6.781 7.23 7.863 7.034 9.08c-.099.607.002 1.244.334 1.761.331.517.895.919 1.73 1.055l.28.031a2.2 2.2 0 0 0 1.579-.513c.465-.395.767-.975.867-1.588.1-.612-.002-1.254-.339-1.774-.294-.453-.77-.816-1.454-.985z"
              ></path>
            </mask>
            <g mask="url(#mask0_1660_1646)">
              <path
                fill="#fff"
                d="m14.5 10 2 1.086L19 8.143l-1-2.714-2-3.8L11.5 0H7L2.5 3.8 1 10.314 3 15.2l2.5 2.171 5 1.629v-2.714z"
              ></path>
            </g>
            <path
              fill="#FB275D"
              d="M12.613 18.573a1.63 1.63 0 0 1-1.411-.798 1.49 1.49 0 0 1 .044-1.57l2.907-4.445a1.63 1.63 0 0 1 1.434-.76 1.62 1.62 0 0 1 1.389.834c.27.5.237 1.1-.088 1.568l-2.905 4.446a1.63 1.63 0 0 1-1.37.725"
            ></path>
            <path
              fill="#FC0"
              d="M17.566 18.574a1.63 1.63 0 0 1-1.41-.797 1.49 1.49 0 0 1 .045-1.566l2.9-4.435A1.63 1.63 0 0 1 20.539 11a1.62 1.62 0 0 1 1.397.839c.27.503.23 1.108-.104 1.575l-2.9 4.435c-.295.45-.81.724-1.366.724"
            ></path>
            <path
              fill="#00CA72"
              d="M22.393 15.375c.887 0 1.607.718 1.607 1.605 0 .886-.72 1.604-1.607 1.604a1.606 1.606 0 0 1-1.608-1.604c0-.887.72-1.605 1.608-1.605"
            ></path>
            <path
              stroke="#1E1E1E"
              d="M22.393 15.375c.887 0 1.607.718 1.607 1.605 0 .886-.72 1.604-1.607 1.604a1.606 1.606 0 0 1-1.608-1.604c0-.887.72-1.605 1.608-1.605Z"
            ></path>
          </svg>
        </button>
      </div>
    </ButtonGroup>
  );
};
